/**
 * Control assignment — the GM-only owner-of-record / co-driver / allegiance reassignment the
 * permission gate reads live off state, plus the visibility grant the projection reads
 * (`decisions/0179`: seeing a unit is its own grant, never derived from controlling it).
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { isSide } from "../types";
import { changed, requireCombatant, withCombatant, type EncounterHandler } from "./context";

export const applySetController: EncounterHandler<"set_controller"> = (state, cmd) => {
  // GM-only manual-tier reassignment (see the SetControllerCommand docstring for
  // why this is not a force-tier command). Writes TWO different levels of the
  // struct: `controllerSlotId` and the two grant lists on the combatant itself,
  // `allegiance` on its frozen `base` — never routed through `edit_combatant_base`
  // for the latter. `allegiance` is enum-guarded (an omitted value is the legitimate
  // "leave alone" case, never rejected); `controllerSlotId` needs no guard (every
  // string is a legal slot id, and `null` is the documented GM-owned sentinel), and
  // neither do the two lists (same reason, per entry).
  if (cmd.allegiance !== undefined && !isSide(cmd.allegiance)) {
    return err(new CommandError(`set_controller: unknown allegiance ${JSON.stringify(cmd.allegiance)}`));
  }
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  // Canonicalize a never-set `undefined` to `null` for the inverse: both read as
  // "GM-owned" everywhere `controllerSlotId` is consulted (the field's own doc), and
  // `controllerSlotId` is NOT optional on this command's type, so there is no way to
  // represent "never set" on the wire regardless — `null` is the honest prior value.
  const prevController = c.controllerSlotId ?? null;
  const prevAllegiance = c.base.allegiance;
  const prevCoControllers = c.coControllerSlotIds;
  const prevViewGrants = c.visibilityGrantSlotIds;
  const nextBase = cmd.allegiance !== undefined ? { ...c.base, allegiance: cmd.allegiance } : c.base;
  // The co-list is REPLACE-not-merge, and omitted means "leave alone" (the same
  // discipline `allegiance` uses above). Normalization is the reducer's job, not the
  // caller's: dedupe, and drop the owner of record if it appears — so the two
  // fields can never name the same slot twice and `controlsCombatant` can never
  // double-count. Note it normalizes against the command's NEW owner, not the old one,
  // since a single command can move both in one breath.
  const nextCoControllers =
    cmd.coControllerSlotIds === undefined
      ? prevCoControllers
      : [...new Set(cmd.coControllerSlotIds)].filter((s) => s !== cmd.controllerSlotId);
  // The visibility grant list (`decisions/0179`): same replace-not-merge and
  // omitted-means-leave-alone discipline as the co-list — and one deliberate DIFFERENCE,
  // which is the whole reason these two normalizations are not one helper. This list is
  // **deduped only**; the owner of record is NOT filtered out of it. The owner sees via
  // `hasFullView`'s other branch, so a redundant entry is harmless, whereas dropping it
  // would silently strip a granted player's view the moment a later `set_controller`
  // re-homed the unit away from them (C1.4's hazard in a new coat). Do not "fix" this into
  // symmetry with the line above.
  const nextViewGrants =
    cmd.visibilityGrantSlotIds === undefined ? prevViewGrants : [...new Set(cmd.visibilityGrantSlotIds)];
  const next = withCombatant(state, {
    ...c,
    controllerSlotId: cmd.controllerSlotId,
    base: nextBase,
    ...(nextCoControllers === undefined ? {} : { coControllerSlotIds: nextCoControllers }),
    ...(nextViewGrants === undefined ? {} : { visibilityGrantSlotIds: nextViewGrants }),
  });
  return ok({
    state: next,
    inverse: {
      type: "set_controller",
      actorId: cmd.actorId,
      combatantId: c.id,
      controllerSlotId: prevController,
      // Only carry `allegiance` in the inverse when THIS command touched it — a
      // command that left allegiance alone must invert to one that also leaves it
      // alone, not one that redundantly re-asserts the (unchanged) prior value.
      ...(cmd.allegiance !== undefined ? { allegiance: prevAllegiance } : {}),
      // Same discipline for the co-list. The one wrinkle `allegiance` doesn't have: a
      // combatant that never had a co-list reads `undefined`, and the inverse must
      // restore *that*, not `[]` — but the field is optional, so "restore undefined"
      // is spelled by omitting it, which is also how "leave alone" is spelled. Those
      // coincide correctly here: if the forward command set a co-list where there was
      // none, the inverse omits the field, and applying it leaves the co-list as the
      // forward command left it — WRONG. So an omitted-prior inverts to an explicit
      // empty list instead, which is the honest "there were no co-drivers" value.
      ...(cmd.coControllerSlotIds !== undefined ? { coControllerSlotIds: prevCoControllers ?? [] } : {}),
      // And the same for the view list, wrinkle included: an omitted-prior inverts to an
      // explicit `[]`, never an omission, or the inverse of "granted a view where there was
      // none" would read as "leave the view list alone" and the grant would survive its own
      // undo.
      ...(cmd.visibilityGrantSlotIds !== undefined ? { visibilityGrantSlotIds: prevViewGrants ?? [] } : {}),
    },
    events: changed(c.id),
  });
};
